import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';

interface GoalWorkbookJunction {
  id: string;
  user_id: string;
  goal_id: string;
  workbook_id: string;
  agent_thread_id: string | null;
  created_at: string;
  updated_at: string;
}

interface CreateJunctionParams {
  goalId: string;
  workbookId: string;
  agentThreadId: string;
}

/**
 * Creates a junction record linking a goal and workbook with agent thread
 */
export async function createGoalWorkbookJunction(params: CreateJunctionParams): Promise<GoalWorkbookJunction> {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user.id;
  if (!userId) throw new Error('User must be authenticated');

  const { data, error } = await supabase
    .from('planner_goals_workbooks')
    .insert({
      user_id: userId,
      goal_id: params.goalId,
      workbook_id: params.workbookId,
      agent_thread_id: params.agentThreadId
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating goal-workbook junction:', error);
    throw error;
  }

  return data;
}

/**
 * Deletes a junction record
 */
export async function deleteGoalWorkbookJunction(junctionId: string): Promise<void> {
  const { error } = await supabase
    .from('planner_goals_workbooks')
    .delete()
    .eq('id', junctionId);

  if (error) {
    console.error('Error deleting goal-workbook junction:', error);
    throw error;
  }
}

/**
 * Gets the associated goal for a workbook
 */
export async function getWorkbookGoal(workbookId: string): Promise<any> {
  const { data, error } = await supabase
    .from('planner_goals_workbooks')
    .select(`
      goal_id,
      planner_goals (
        id,
        title,
        description,
        openai_thread_id,
        created_at
      )
    `)
    .eq('workbook_id', workbookId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // No rows found
    console.error('Error fetching workbook goal:', error);
    throw error;
  }

  return data?.planner_goals;
}

/**
 * Gets the associated workbook for a goal
 */
export async function getGoalWorkbook(goalId: string): Promise<any> {
  const { data, error } = await supabase
    .from('planner_goals_workbooks')
    .select(`
      workbook_id,
      workbook (
        id,
        title,
        created_at,
        updated_at
      )
    `)
    .eq('goal_id', goalId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // No rows found
    console.error('Error fetching goal workbook:', error);
    throw error;
  }

  return data?.workbook;
}

/**
 * Hook for managing goal-workbook junction operations
 */
export function useGoalWorkbookJunction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createJunctionMutation = useMutation({
    mutationFn: createGoalWorkbookJunction,
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['goal-workbook-junctions'] });
      queryClient.invalidateQueries({ queryKey: ['workbook-goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal-workbooks'] });
    },
    onError: (error: any) => {
      console.error('Error creating goal-workbook junction:', error);
      toast({
        title: "Error",
        description: "Failed to link goal and workbook",
        variant: "destructive",
      });
    },
  });

  const deleteJunctionMutation = useMutation({
    mutationFn: deleteGoalWorkbookJunction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goal-workbook-junctions'] });
      queryClient.invalidateQueries({ queryKey: ['workbook-goals'] });
      queryClient.invalidateQueries({ queryKey: ['goal-workbooks'] });
      toast({
        title: "Success",
        description: "Goal and workbook unlinked",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting goal-workbook junction:', error);
      toast({
        title: "Error",
        description: "Failed to unlink goal and workbook",
        variant: "destructive",
      });
    },
  });

  return {
    createJunction: createJunctionMutation.mutateAsync,
    deleteJunction: deleteJunctionMutation.mutate,
    isCreating: createJunctionMutation.isPending,
    isDeleting: deleteJunctionMutation.isPending,
  };
}

/**
 * Hook to query the associated goal for a workbook
 */
export function useWorkbookGoal(workbookId: string) {
  return useQuery({
    queryKey: ['workbook-goal', workbookId],
    queryFn: () => getWorkbookGoal(workbookId),
    enabled: !!workbookId,
  });
}

/**
 * Hook to query the associated workbook for a goal
 */
export function useGoalWorkbook(goalId: string) {
  return useQuery({
    queryKey: ['goal-workbook', goalId],
    queryFn: () => getGoalWorkbook(goalId),
    enabled: !!goalId,
  });
} 